import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ThemeContext } from "./ThemeContext";
import { ThemeConfig } from "@/themes/types";
import {
  loadThemeConfig,
  injectCSSVariables,
  loadStylesheet,
  getThemeStylesheetId,
  resetThemeCSSVariables,
  removeAllThemeStylesheets,
} from "@/themes/theme-loader";
import { getItem, getUserToken, setItem } from "@/services/storage-data";
import { fetchBrandTheme } from "./brand-theme";
import { UI_STYLESHEET_MODERN, uiStylesheetToTheme } from "./ui-stylesheet";

const THEME_STORAGE_KEY = "selectedTheme";

interface MultiThemeProviderProps {
  children: React.ReactNode;
}

/**
 * MultiThemeProvider
 * Manages theme loading, CSS injection, and provides theme context to the app
 */
export const MultiThemeProvider: React.FC<MultiThemeProviderProps> = ({
  children,
}) => {
  const [currentTheme, setCurrentTheme] = useState<string | null>(null);
  const [themeConfig, setThemeConfig] = useState<ThemeConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Theme the latest call asked for, tracked in a ref because loading is asynchronous:
   * the request is known before the config (and therefore the rendered state) is.
   * `undefined` means no request has been made yet, which `null` (the base theme) does not.
   */
  const requestedThemeRef = useRef<string | null | undefined>(undefined);
  /** Incremented per request so a slower earlier load cannot apply over a newer one. */
  const requestIdRef = useRef<number>(0);

  /**
   * Strip whatever a previous theme put in the DOM.
   *
   * Deliberately brand-agnostic instead of undoing the config we last applied: a switch can be
   * requested while an earlier load is still in flight, so the config in state is not a reliable
   * record of what is actually on the page.
   */
  const clearAppliedTheme = useCallback(() => {
    resetThemeCSSVariables();
    removeAllThemeStylesheets();
    if (typeof window !== "undefined") {
      document.documentElement.removeAttribute("data-theme");
    }
  }, []);

  /**
   * Load theme configuration and apply styles
   */
  const loadAndApplyTheme = useCallback(
    async (themeName: string | null) => {
      // Re-applying the theme already on the page would clear it and paint the base theme for
      // the duration of the dynamic import. This happens on every load, because validate
      // confirms the theme the saved value already applied on mount.
      if (requestedThemeRef.current === themeName) {
        return;
      }
      requestedThemeRef.current = themeName;

      const requestId = ++requestIdRef.current;
      const isStale = () => requestIdRef.current !== requestId;

      clearAppliedTheme();

      // If no theme, reset to base
      if (!themeName) {
        setThemeConfig(null);
        setCurrentTheme(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Load theme config
        const config = await loadThemeConfig(themeName);

        // A newer request already cleared the DOM and owns the theme from here on.
        if (isStale()) {
          return;
        }

        if (config) {
          // Apply CSS variables
          if (config.styles.cssVariables) {
            injectCSSVariables(config.styles.cssVariables);
          }

          // Load custom stylesheet if specified
          if (config.styles.customStylesheet) {
            loadStylesheet(
              config.styles.customStylesheet,
              getThemeStylesheetId(config.name),
            );
          }

          // Set data-theme attribute on HTML element
          if (typeof window !== "undefined") {
            document.documentElement.setAttribute("data-theme", themeName);
          }

          setThemeConfig(config);
          setCurrentTheme(themeName);
        } else {
          console.warn(`Theme "${themeName}" not found, using base theme`);
          setThemeConfig(null);
          setCurrentTheme(null);
        }
      } catch (error) {
        if (isStale()) {
          return;
        }
        console.error(`Error loading theme "${themeName}":`, error);
        // Allow a later call to retry the theme this request failed to apply.
        requestedThemeRef.current = undefined;
        setThemeConfig(null);
        setCurrentTheme(null);
      } finally {
        if (!isStale()) {
          setIsLoading(false);
        }
      }
    },
    [clearAppliedTheme],
  );

  /**
   * Initialize theme on mount. A saved theme (written by validate for signed-in users)
   * wins; otherwise the print house behind the current domain decides, so no hostname
   * list has to be maintained in the frontend.
   */
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let isMounted = true;

    const initializeTheme = async () => {
      const savedTheme = getItem(THEME_STORAGE_KEY);
      if (savedTheme) {
        await loadAndApplyTheme(savedTheme);
        return;
      }

      const brandTheme = await fetchBrandTheme();
      if (!isMounted) {
        return;
      }
      // The domain lookup only decides for visitors we know nothing about yet. If validate
      // resolved first it already picked the signed-in print house's theme, which wins.
      if (requestedThemeRef.current !== undefined) {
        return;
      }
      if (brandTheme) {
        setItem(THEME_STORAGE_KEY, brandTheme);
        await loadAndApplyTheme(brandTheme);
        return;
      }
      // A signed-in user's style comes from the print house's UI_STYLESHEET preference,
      // applied by validate. Painting a stand-in first would swap the layout mid-render.
      if (getUserToken()) {
        return;
      }
      // Visitors (login, invite) see Modern until they sign in.
      await loadAndApplyTheme(uiStylesheetToTheme(UI_STYLESHEET_MODERN));
    };

    initializeTheme();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Set theme and persist to localStorage
   */
  const setTheme = useCallback(
    (themeName: string | null) => {
      if (themeName) {
        setItem(THEME_STORAGE_KEY, themeName);
      } else {
        localStorage.removeItem(THEME_STORAGE_KEY);
      }
      loadAndApplyTheme(themeName);
    },
    [loadAndApplyTheme],
  );

  /**
   * Get page override component by name
   */
  const getPageOverride = useCallback(
    <T = any,>(pageName: string): React.ComponentType<T> | null => {
      if (!themeConfig || !themeConfig.pages) {
        return null;
      }
      return (themeConfig.pages[pageName] as React.ComponentType<T>) || null;
    },
    [themeConfig],
  );

  /**
   * Get component override by name
   */
  const getComponentOverride = useCallback(
    <T = any,>(componentName: string): React.ComponentType<T> | null => {
      if (!themeConfig || !themeConfig.components) {
        return null;
      }
      return (
        (themeConfig.components[componentName] as React.ComponentType<T>) ||
        null
      );
    },
    [themeConfig],
  );

  /**
   * Get icon override by name
   */
  const getIconOverride = useCallback(
    <T = any,>(iconName: string): React.ComponentType<T> | null => {
      if (!themeConfig || !themeConfig.icons) {
        return null;
      }
      return (themeConfig.icons[iconName] as React.ComponentType<T>) || null;
    },
    [themeConfig],
  );

  /**
   * Memoized context value
   */
  const contextValue = useMemo(
    () => ({
      currentTheme,
      themeConfig,
      isLoading,
      getPageOverride,
      getComponentOverride,
      getIconOverride,
      setTheme,
    }),
    [
      currentTheme,
      themeConfig,
      isLoading,
      getPageOverride,
      getComponentOverride,
      getIconOverride,
      setTheme,
    ],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};
